import { useEffect, useRef, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { WifiOff } from 'lucide-react';

// Daily Prebuilt via daily-js: no built-in leave button (we own Leave), device
// check screen on entry, and banners when either side's connection drops.
export const DailyCall = ({ roomUrl, token, onLeft }) => {
  const containerRef = useRef(null);
  const onLeftRef = useRef(onLeft);
  onLeftRef.current = onLeft;
  const [remoteGone, setRemoteGone] = useState(null);   // name of dropped participant
  const [localDropped, setLocalDropped] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const name = (p) => p?.user_name || 'The other person';
    let alive = true;
    let frame = null;
    // daily-js allows only one instance at a time; React StrictMode (dev) mounts
    // effects twice and destroy() is async, so wait for any previous instance.
    const start = async () => {
      const prev = DailyIframe.getCallInstance();
      if (prev) { try { await prev.destroy(); } catch { /* ignore */ } }
      if (!alive || !containerRef.current) return;
      frame = DailyIframe.createFrame(containerRef.current, {
        showLeaveButton: false,
        showFullscreenButton: true,
        iframeStyle: { width: '100%', height: '100%', border: '0', background: '#000' },
      });
      frame
        .on('participant-left', (e) => { if (!e?.participant?.local) setRemoteGone(name(e.participant)); })
        .on('participant-joined', (e) => { if (!e?.participant?.local) setRemoteGone(null); })
        .on('network-connection', (e) => {
          if (e?.event === 'interrupted') setLocalDropped(true);
          if (e?.event === 'connected') setLocalDropped(false);
        })
        // Fires when the other side ends the session (room deleted → we're ejected).
        .on('left-meeting', () => { if (alive) onLeftRef.current?.(); });
      frame.join({ url: roomUrl, token }).catch(() => {});
    };
    start();
    return () => { alive = false; if (frame) frame.destroy().catch(() => {}); };
  }, [roomUrl, token]);

  return (
    <div className="lobby-iframe" style={{ position: 'relative' }} data-testid="lobby-iframe">
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {(remoteGone || localDropped) && (
        <div className="lobby-conn-banner" data-testid="lobby-connection-banner">
          <WifiOff size={16} />
          <span>
            {localDropped
              ? 'Your connection dropped — reconnecting… please stay on this screen.'
              : `${remoteGone}'s connection was lost. Please stay in the meeting — they should be back shortly.`}
          </span>
        </div>
      )}
    </div>
  );
};
